import React, { useState } from 'react';

interface ServiceSelection {
  hair: boolean;
  makeup: boolean;
  sareeDraping: boolean;
}

interface EventServicesData {
  name: string;
  phone: string;
  countryCode: string;
  services: {
    march13: ServiceSelection;
    march14: ServiceSelection;
  };
}

const EventServicesPage: React.FC = () => {
  const [formData, setFormData] = useState<EventServicesData>({
    name: '',
    phone: '',
    countryCode: '+1',
    services: {
      march13: {
        hair: false,
        makeup: false,
        sareeDraping: false
      },
      march14: {
        hair: false,
        makeup: false,
        sareeDraping: false
      }
    }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleServiceChange = (date: 'march13' | 'march14', service: keyof ServiceSelection, checked: boolean) => {
    setFormData({
      ...formData,
      services: {
        ...formData.services,
        [date]: {
          ...formData.services[date],
          [service]: checked
        }
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Check if at least one service is selected
    const hasAnyService =
      Object.values(formData.services.march13).some(v => v) ||
      Object.values(formData.services.march14).some(v => v);

    if (!hasAnyService) {
      setError('Please select at least one service');
      setIsSubmitting(false);
      return;
    }

    try {
      const airtableApiKey = process.env.REACT_APP_AIRTABLE_API_KEY;
      const airtableBaseId = process.env.REACT_APP_AIRTABLE_BASE_ID;

      if (!airtableApiKey || !airtableBaseId) {
        throw new Error('Airtable configuration missing. Please add API key and Base ID to environment variables.');
      }

      const response = await fetch(`https://api.airtable.com/v0/${airtableBaseId}/EventServices`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${airtableApiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fields: {
            'Name': formData.name,
            'Phone': formData.countryCode + ' ' + formData.phone,
            'March 13 - Hair': formData.services.march13.hair ? 'Yes' : 'No',
            'March 13 - Makeup': formData.services.march13.makeup ? 'Yes' : 'No',
            'March 13 - Saree Draping': formData.services.march13.sareeDraping ? 'Yes' : 'No',
            'March 14 - Hair': formData.services.march14.hair ? 'Yes' : 'No',
            'March 14 - Makeup': formData.services.march14.makeup ? 'Yes' : 'No',
            'March 14 - Saree Draping': formData.services.march14.sareeDraping ? 'Yes' : 'No',
          }
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error('Airtable API Error:', errorData);
        throw new Error(errorData?.error?.message || `HTTP ${response.status}: Failed to submit form`);
      }

      setIsSubmitted(true);
    } catch (err) {
      setError('Failed to submit form. Please try again later.');
      console.error('Error submitting form:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="max-w-2xl mx-auto flex items-center justify-center" style={{ minHeight: '60vh', marginTop: '1cm' }}>
        <div className="clean-card p-12 text-center animate-slide-up">
          <div className="w-16 h-16 bg-neutral-900 rounded-full mx-auto mb-6 flex items-center justify-center">
            <span className="text-white text-2xl">✓</span>
          </div>
          <h1 className="text-4xl font-display font-medium text-sage-800 mb-4">
            Thank you for your interest!
          </h1>
          <p className="text-sage-grey-600 leading-relaxed mb-8">
            We've received your service request and will be in touch with more details.
          </p>
          <div className="section-divider"></div>
          <p className="text-neutral-500">
            See you in Khajuraho!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-display font-medium text-sage-800 mb-4">
          Event Services
        </h1>
        <div className="elegant-divider"></div>
        <p className="text-sage-grey-600 leading-relaxed">
          We're delighted to offer salon services at the venue
        </p>
      </div>

      <div className="glass-card p-8 mb-8">
        <h2 className="text-xl font-display font-medium text-soft-black mb-4">
          About Our Services
        </h2>
        <p className="text-text-gray leading-relaxed mb-6">
          We’ll have a full professional salon set up right at the venue, with services provided on-site by our vetted vendors. You can book and pay them directly at the time of service.
        </p>

        <div className="bg-light-gray p-6 mb-6">
          <h3 className="text-sm font-medium text-soft-black mb-4 uppercase tracking-wider">
            Service Pricing
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-text-gray">Hair Styling</span>
              <span className="font-medium text-soft-black">₹1,000</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-text-gray">Saree Draping</span>
              <span className="font-medium text-soft-black">₹500</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-text-gray">Makeup</span>
              <span className="font-medium text-soft-black">₹3,000</span>
            </div>
          </div>
        </div>

        <p className="text-sm text-text-gray">
          Please fill out the form below to help us gauge interest and prepare accordingly.
          This is not a booking - we're simply collecting information to ensure we have adequate resources.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-md mb-6">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="glass-card p-8">
          <h3 className="text-sm font-medium text-soft-black mb-6 uppercase tracking-wider">
            Your Information
          </h3>

          <div className="mb-6">
            <label htmlFor="name" className="block text-sm font-medium text-soft-black mb-3 uppercase tracking-wider">
              Full Name *
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="minimal-input w-full"
              placeholder="Enter your full name"
              required
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-soft-black mb-3 uppercase tracking-wider">
              Contact Number *
            </label>
            <div className="flex gap-2">
              <select
                value={formData.countryCode}
                onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                className="minimal-input w-20 flex-shrink-0 text-sm"
              >
                <option value="+1">🇺🇸 +1</option>
                <option value="+91">🇮🇳 +91</option>
                <option value="+675">🇵🇬 +675</option>
                <option value="+61">🇦🇺 +61</option>
                <option value="+44">🇬🇧 +44</option>
                <option value="+1">🇨🇦 +1</option>
                <option value="+971">🇦🇪 +971</option>
                <option value="+852">🇭🇰 +852</option>
                <option value="+86">🇨🇳 +86</option>
                <option value="other">Other</option>
              </select>
              <input
                type="tel"
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="minimal-input flex-1"
                placeholder={formData.countryCode === 'other' ? '+XX 123-456-7890' : '(123) 456-7890'}
                required
              />
            </div>
          </div>
        </div>

        {/* March 13 Services */}
        <div className="glass-card p-8">
          <h3 className="text-sm font-medium text-soft-black mb-6 uppercase tracking-wider">
            March 13th - Sangeet
          </h3>
          <div className="space-y-4">
            <label className="flex items-start p-4 sm:p-6 bg-pure-white border border-mid-gray hover:border-soft-black transition-colors duration-300 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.services.march13.hair}
                onChange={(e) => handleServiceChange('march13', 'hair', e.target.checked)}
                className="sr-only"
              />
              <div className={`w-4 h-4 border mr-4 flex items-center justify-center mt-0.5 ${formData.services.march13.hair ? 'border-soft-black bg-soft-black' : 'border-mid-gray'}`}>
                {formData.services.march13.hair && <div className="w-1.5 h-1.5 bg-pure-white"></div>}
              </div>
              <div className="flex-1">
                <div className="font-medium text-soft-black tracking-wide">Hair Styling</div>
                <div className="text-sm text-text-gray">Professional hair styling and updo</div>
              </div>
            </label>

            <label className="flex items-start p-4 sm:p-6 bg-pure-white border border-mid-gray hover:border-soft-black transition-colors duration-300 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.services.march13.makeup}
                onChange={(e) => handleServiceChange('march13', 'makeup', e.target.checked)}
                className="sr-only"
              />
              <div className={`w-4 h-4 border mr-4 flex items-center justify-center mt-0.5 ${formData.services.march13.makeup ? 'border-soft-black bg-soft-black' : 'border-mid-gray'}`}>
                {formData.services.march13.makeup && <div className="w-1.5 h-1.5 bg-pure-white"></div>}
              </div>
              <div className="flex-1">
                <div className="font-medium text-soft-black tracking-wide">Makeup</div>
                <div className="text-sm text-text-gray">Professional makeup application</div>
              </div>
            </label>

            <label className="flex items-start p-4 sm:p-6 bg-pure-white border border-mid-gray hover:border-soft-black transition-colors duration-300 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.services.march13.sareeDraping}
                onChange={(e) => handleServiceChange('march13', 'sareeDraping', e.target.checked)}
                className="sr-only"
              />
              <div className={`w-4 h-4 border mr-4 flex items-center justify-center mt-0.5 ${formData.services.march13.sareeDraping ? 'border-soft-black bg-soft-black' : 'border-mid-gray'}`}>
                {formData.services.march13.sareeDraping && <div className="w-1.5 h-1.5 bg-pure-white"></div>}
              </div>
              <div className="flex-1">
                <div className="font-medium text-soft-black tracking-wide">Saree Draping</div>
                <div className="text-sm text-text-gray">Traditional saree draping service</div>
              </div>
            </label>
          </div>
        </div>

        {/* March 14 Services */}
        <div className="glass-card p-8">
          <h3 className="text-sm font-medium text-soft-black mb-6 uppercase tracking-wider">
            March 14th - Shaadi & Reception
          </h3>
          <div className="space-y-4">
            <label className="flex items-start p-4 sm:p-6 bg-pure-white border border-mid-gray hover:border-soft-black transition-colors duration-300 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.services.march14.hair}
                onChange={(e) => handleServiceChange('march14', 'hair', e.target.checked)}
                className="sr-only"
              />
              <div className={`w-4 h-4 border mr-4 flex items-center justify-center mt-0.5 ${formData.services.march14.hair ? 'border-soft-black bg-soft-black' : 'border-mid-gray'}`}>
                {formData.services.march14.hair && <div className="w-1.5 h-1.5 bg-pure-white"></div>}
              </div>
              <div className="flex-1">
                <div className="font-medium text-soft-black tracking-wide">Hair Styling</div>
                <div className="text-sm text-text-gray">Professional hair styling and updo</div>
              </div>
            </label>

            <label className="flex items-start p-4 sm:p-6 bg-pure-white border border-mid-gray hover:border-soft-black transition-colors duration-300 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.services.march14.makeup}
                onChange={(e) => handleServiceChange('march14', 'makeup', e.target.checked)}
                className="sr-only"
              />
              <div className={`w-4 h-4 border mr-4 flex items-center justify-center mt-0.5 ${formData.services.march14.makeup ? 'border-soft-black bg-soft-black' : 'border-mid-gray'}`}>
                {formData.services.march14.makeup && <div className="w-1.5 h-1.5 bg-pure-white"></div>}
              </div>
              <div className="flex-1">
                <div className="font-medium text-soft-black tracking-wide">Makeup</div>
                <div className="text-sm text-text-gray">Professional makeup application</div>
              </div>
            </label>

            <label className="flex items-start p-4 sm:p-6 bg-pure-white border border-mid-gray hover:border-soft-black transition-colors duration-300 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.services.march14.sareeDraping}
                onChange={(e) => handleServiceChange('march14', 'sareeDraping', e.target.checked)}
                className="sr-only"
              />
              <div className={`w-4 h-4 border mr-4 flex items-center justify-center mt-0.5 ${formData.services.march14.sareeDraping ? 'border-soft-black bg-soft-black' : 'border-mid-gray'}`}>
                {formData.services.march14.sareeDraping && <div className="w-1.5 h-1.5 bg-pure-white"></div>}
              </div>
              <div className="flex-1">
                <div className="font-medium text-soft-black tracking-wide">Saree Draping</div>
                <div className="text-sm text-text-gray">Traditional saree draping service</div>
              </div>
            </label>
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="minimal-button w-full text-lg py-4 min-h-[48px] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-white border-t-transparent animate-spin mr-2"></div>
              Submitting...
            </span>
          ) : (
            'Submit Interest Form'
          )}
        </button>
      </form>

      <div className="mt-8 text-center text-xs text-text-gray uppercase tracking-wider">
        Questions? <a href="mailto:shivangiandishan@gmail.com" className="text-soft-black underline">Contact us directly</a>
      </div>
    </div>
  );
};

export default EventServicesPage;
